/**
 * Brazilian Portuguese.
 *
 * Brazil is named in §0 as one of the markets the commercial opening is in, and
 * in §7.5 as a market where in-chat commerce is the core business use case
 * rather than an edge feature — so this is not a courtesy locale.
 *
 * Needs review by a native speaker before shipping; see the note in `ar.ts`.
 */
import type { MessageKey } from './en.ts';

export const ptBR: Partial<Record<MessageKey, string>> = {
  'app.opening': 'Abrindo o cofre…',
  'app.empty.title': 'Escolha uma conversa',
  'app.empty.body':
    'Suas mensagens são criptografadas de ponta a ponta. O servidor apenas repassa envelopes lacrados entre vocês — ele não consegue abri-los, e nem quem conseguir acesso a ele.',
  'app.identityAlert.title': 'A chave de identidade mudou',
  'app.identityAlert.body':
    'A chave de identidade de {name} mudou. Pode ser que tenham reinstalado o aplicativo — ou que alguém esteja se passando por eles.',
  'app.identityAlert.accept': 'Aceitar a nova chave',
  'app.identityAlert.dismiss': 'Ignorar',

  'onboarding.invited': '{name} convidou você.',
  'onboarding.invited.detail': 'Depois do cadastro, sua conversa com essa pessoa já estará esperando.',
  'onboarding.intro':
    'Mensagens criptografadas de ponta a ponta. Suas chaves são geradas aqui, no seu aparelho, e nunca saem dele.',
  'onboarding.username.label': 'Nome de usuário',
  'onboarding.username.hint': 'De 3 a 32 caracteres, em um único sistema de escrita.',
  'onboarding.displayName.label': 'Nome de exibição',
  'onboarding.phone.label': 'Número de telefone — opcional',
  'onboarding.phone.hint':
    'Só para que quem já tem o seu número consiga te encontrar. Não é o nome da sua conta, não é como você entra, e removê-lo depois não leva mais nada junto.',
  'onboarding.submit': 'Criar conta',
  'onboarding.submit.busy': 'Gerando chaves…',
  'onboarding.warning':
    'Não há senha nem recuperação. Sua identidade é uma chave privada guardada neste navegador — se você limpar os dados do site, precisará se cadastrar de novo.',
  'onboarding.username.error.too_short': 'Pelo menos 3 caracteres.',
  'onboarding.username.error.too_long': 'No máximo 32 caracteres.',
  'onboarding.username.error.bad_start': 'Não pode começar com ponto, hífen ou sublinhado.',
  'onboarding.username.error.disallowed_character': 'Contém um caractere que não é permitido.',
  'onboarding.username.error.mixed_script':
    'Mistura dois sistemas de escrita. Escolha um, para que ninguém possa imitar seu nome.',
  'onboarding.username.error.reserved': 'Esse nome é reservado.',

  'status.online': 'On-line',
  'status.connecting': 'Conectando…',
  'status.offline': 'Off-line',
  'sidebar.invite': 'Convidar amigos',
  'sidebar.findByPhone': 'Buscar por número de telefone',
  'sidebar.newChat': 'Nova conversa',
  'sidebar.search': 'Buscar conversas',
  'sidebar.conversations': 'Conversas',
  'sidebar.empty': 'Ainda não há conversas.',
  'sidebar.noMatches': 'Nenhum resultado',
  'sidebar.noMessages': 'Ainda não há mensagens',
  'sidebar.group': 'Grupo',
  'sidebar.notifications.on': 'Notificações ativadas',
  'sidebar.notifications.denied': 'Notificações bloqueadas pelo navegador',
  'sidebar.notifications.enable': 'Ativar notificações',

  'thread.back': 'Voltar para as conversas',
  'thread.typing': '{count, plural, one {{names} está digitando…} other {{names} estão digitando…}}',
  'thread.members': '{count, plural, one {# integrante} other {# integrantes}}',
  'thread.verifySafety': 'Verificar o número de segurança',
  'thread.encrypted':
    'As mensagens desta conversa são criptografadas de ponta a ponta. Nem o servidor consegue lê-las.',
  'thread.replyingTo': 'Respondendo a: {preview}',
  'thread.attachment': 'Anexo',
  'thread.cancelReply': 'Cancelar resposta',
  'thread.recording': 'Gravando… solte para enviar',
  'thread.cancelRecording': 'Cancelar gravação',
  'thread.composer': 'Mensagem',
  'thread.send': 'Enviar',
  'thread.voice.unavailable.title':
    'A gravação de voz exige HTTPS — o navegador bloqueia o microfone neste endereço',
  'thread.voice.unavailable.label': 'Gravação de voz indisponível sem HTTPS',
  'thread.voice.start': 'Gravar uma mensagem de voz',
  'thread.voice.stop': 'Parar e enviar',
  'thread.voice.error.title': 'Não foi possível gravar',

  'bubble.deleted': 'Esta mensagem foi apagada',
  'bubble.actions': 'Ações da mensagem',
  'bubble.reply': 'Responder',
  'profile.profiles': 'Perfis',
  'profile.profiles.explain': 'Contas separadas neste dispositivo: trabalho, pessoal, um negócio. Todas ficam conectadas, então uma mensagem para qualquer uma notifica você.',
  'profile.profiles.add': 'Adicionar um perfil',
  'profile.profiles.create': 'Criar',
  'profile.profiles.namePlaceholder': 'Trabalho, Pessoal…',
  'profile.profiles.unnamed': 'Perfil sem nome',
  'profile.profiles.remove': 'Remover perfil',
  'profile.profiles.removeConfirm': 'Apagar esta conta para sempre?',
  'profile.profiles.warning': 'Um novo perfil é uma nova conta com seu próprio nome de usuário — não outra visão desta. Também não pode ser recuperada.',
  'profile.build': 'Versão {build}',
  'profile.name.edit': 'Alterar nome',
  'profile.name.save': 'Salvar',
  'profile.name.cancel': 'Cancelar',
  'profile.name.failed': 'Não foi possível alterar o nome. Verifique a conexão e tente novamente.',
  'profile.delete': 'Excluir conta',
  'profile.delete.explain': 'Isso remove sua conta do servidor para sempre. Suas chaves existem apenas aqui, então não há volta nem nada a recuperar.',
  'profile.delete.others': 'As mensagens que você já enviou permanecem nos aparelhos que as receberam. Ninguém é avisado — descobrem se te escreverem.',
  'profile.delete.confirmPrompt': 'Digite seu nome de usuário para confirmar',
  'profile.delete.confirm': 'Excluir definitivamente',
  'profile.delete.failed': 'Não foi possível excluir a conta. Verifique a conexão e tente novamente.',
  'thread.recipientGone': 'Esta pessoa não está mais no VAV. Suas mensagens não chegarão a ela.',
  'thread.recipientGone.title': 'Esta conta foi excluída',
  'profile.avatar.change': 'Alterar foto',
  'profile.avatar.remove': 'Remover foto',
  'profile.avatar.explain': 'Sua foto é criptografada e enviada apenas para as pessoas com quem você fala. Estranhos veem suas iniciais.',
  'avatar.crop.title': 'Ajustar a foto',
  'avatar.crop.hint': 'Arraste para posicionar, deslize para ampliar. O que fica dentro do círculo é o que todos veem.',
  'avatar.crop.zoom': 'Zoom',
  'avatar.crop.loadFailed': 'Não foi possível ler esta foto. Pode ser um formato que o app não aceita — tente outra foto.',
  'call.incoming.audio': 'Chamada de voz recebida',
  'call.incoming.video': 'Chamada de vídeo recebida',
  'call.accept': 'Atender',
  'call.decline': 'Recusar',
  'call.outgoing': 'Chamando…',
  'call.cancel': 'Cancelar',
  'call.hangup': 'Encerrar',
  'call.mute': 'Mudo',
  'call.unmute': 'Ativar som',
  'call.cameraOff': 'Desligar câmera',
  'call.cameraOn': 'Ligar câmera',
  'call.connecting': 'Conectando…',
  'call.ended.declined': 'Chamada recusada',
  'call.ended.busy': 'Ocupado',
  'call.ended.hangup': 'Chamada encerrada',
  'call.ended.failed': 'Não foi possível iniciar a chamada',
  'call.ended.unanswered': 'Sem resposta',
  'thread.call.audio': 'Chamada de voz',
  'thread.call.video': 'Chamada de vídeo',
  'call.legFailed': 'Não foi possível conectar',
  'call.turnMissing': 'O servidor de retransmissão (TURN) ainda não está configurado — chamadas entre redes diferentes precisam dele',
  'call.forProfile': 'Para seu perfil {name}',
  'call.toSpeaker': 'Viva-voz',
  'call.toEarpiece': 'Fone',
  'onboarding.link.start': 'Já tenho uma conta — vincular este aparelho',
  'onboarding.link.title': 'Vincular este aparelho',
  'onboarding.link.hint': 'Em um aparelho que já tem sua conta: Meus dados, Vincular novo aparelho, e escaneie este código ou cole-o.',
  'onboarding.link.verifyHint': 'Confira se estes dígitos aparecem também no aparelho que aprova.',
  'onboarding.link.copy': 'Copiar código de vinculação',
  'onboarding.link.copied': 'Copiado ✓',
  'onboarding.link.waiting': 'Aguardando aprovação…',
  'onboarding.link.failed': 'A vinculação não foi concluída. Recomece nos dois aparelhos.',
  'profile.device.remove': 'Desconectar',
  'profile.logout': 'Sair neste aparelho',
  'profile.logout.explain': 'Sua conta permanece nos seus outros aparelhos. Tudo o que está salvo neste aparelho será apagado dele.',
  'profile.logout.confirm': 'Sair',
  'link.approve.stranded': 'Este navegador não tem a sua conta e por isso não pode aprovar o novo aparelho. Abra o link (ou cole o código) no app VAV no aparelho onde está a sua conta.',
  'link.approve.title': 'Aprovar novo aparelho',
  'link.approve.account': 'O novo aparelho será vinculado à conta {name}.',
  'profile.link.start': 'Vincular novo aparelho',
  'profile.link.hint': 'Cole o código de vinculação mostrado no aparelho novo.',
  'profile.link.verifyHint': 'Aprove somente se o aparelho novo mostrar os mesmos dígitos.',
  'profile.link.approve': 'Aprovar aparelho',
  'profile.link.done': 'Aparelho vinculado',
  'profile.link.failed': 'Não foi possível aprovar — o código pode ter expirado.',
  'thread.date.today': 'Hoje',
  'thread.date.yesterday': 'Ontem',
  'contact.delete': 'Excluir contato',
  'contact.delete.explain': 'A conversa e o histórico são removidos apenas deste aparelho. A pessoa não é avisada, e a cópia dela continua com ela.',
  'group.leave': 'Sair do grupo',
  'group.leave.explain': 'Os membros verão que você saiu. O grupo e seu histórico são removidos deste aparelho.',
  'contact.title': 'Dados do contato',
  'contact.verified': 'Verificado',
  'profile.title': 'Meus dados',
  'profile.username': 'Nome de usuário',
  'profile.thisDevice': 'Este dispositivo',
  'profile.devices': 'Dispositivos',
  'profile.devicesLoading': 'Carregando…',
  'profile.devicesNone': 'Não foi possível acessar o servidor.',
  'profile.deviceCurrent': 'este',
  'profile.install': 'Instalar neste dispositivo',
  'profile.install.action': 'Adicionar à tela de início',
  'profile.install.already': 'Você está usando o app instalado.',
  'profile.install.ios': 'Abra o menu Compartilhar e escolha “Adicionar à Tela de Início”.',
  'profile.install.unavailable': 'Seu navegador não está oferecendo a instalação agora, geralmente porque o app já está instalado. Procure VAV na sua lista de apps.',
  'profile.noRecovery': 'Suas chaves existem apenas neste dispositivo. Limpar os dados do navegador ou perder o telefone perde a conta — não há recuperação.',
  'bubble.forward': 'Encaminhar',
  'bubble.select': 'Selecionar',
  'bubble.moreReactions': 'Mais reações',
  'select.cancel': 'Cancelar seleção',
  'select.count': '{count, plural, =0 {Selecionar mensagens} one {# selecionada} other {# selecionadas}}',
  'forward.title': 'Encaminhar para',
  'forward.send': 'Enviar {count, plural, one {# mensagem} other {# mensagens}} para {to, plural, one {# conversa} other {# conversas}}',
  'thread.voice.blocked.how': 'Abra vav.chat no navegador Chrome (não no app instalado), toque no ícone à esquerda da barra de endereço → Permissões → Redefinir permissões. Depois pressione o microfone de novo e aprove.',
  'thread.voice.retry': 'Tentar de novo',
  'thread.voice.blockedByOs': 'Este site tem permissão para o microfone, então o bloqueio não está aqui — o navegador não tem permissão de microfone do telefone.',
  'thread.voice.blockedByOs.how': 'Android: Configurações → Apps → Chrome (ou VAV, se usa o app instalado) → Permissões → Microfone → Permitir.',
  'thread.voice.sendFailed': 'Não foi possível enviar a gravação. Verifique a conexão e tente novamente.',
  'thread.voice.busy': 'O microfone é permitido mas não pôde ser aberto. Provavelmente outro app está usando.',
  'thread.voice.busy.how': 'Feche o que possa estar usando (uma chamada, um assistente de voz, um gravador) e tente de novo. Reiniciar o telefone libera um travado.',
  'thread.unreadFrom': '{count, plural, one {# mensagem nova} other {# mensagens novas}}',
  'thread.voice.blocked': 'O microfone está bloqueado para este site. Uma permissão recusada não pode ser pedida de novo — precisa ser alterada nas configurações.',
  'thread.voice.noMicrophone': 'Nenhum microfone foi encontrado neste dispositivo.',
  'push.prompt.title': 'As notificações estão desligadas',
  'push.prompt.body': 'Ative-as e seu telefone avisará quando chegar uma mensagem, mesmo com o app fechado.',
  'push.prompt.denied': 'Este site está bloqueado de enviar notificações. Permita nas configurações do site e volte.',
  'push.prompt.enable': 'Ativar',
  'push.prompt.dismiss': 'Agora não',
  'quote.you': 'Você',
  'quote.them': 'Eles',
  'quote.unavailable': 'Mensagem indisponível',
  'quote.image': 'Foto',
  'quote.video': 'Vídeo',
  'quote.audio': 'Áudio',
  'quote.voice': 'Mensagem de voz',
  'schedule.title': 'Enviar mais tarde',
  'schedule.explain': 'A mensagem é escrita e selada agora, e sai na hora que você escolher. Até lá é possível cancelá-la no menu ⋯.',
  'schedule.confirm': 'Agendar',
  'bubble.cancelScheduled': 'Cancelar envio agendado',
  'bubble.state.scheduled': 'Agendado',
  'profile.checkUpdate': 'Verificar atualização',
  'profile.checkUpdate.busy': 'Verificando…',
  'profile.checkUpdate.current': 'Você está na versão mais recente.',
  'bubble.edit': 'Editar',
  'bubble.edited': 'editado',
  'thread.editing': 'Editando mensagem',
  'group.edit.title': 'Detalhes do grupo',
  'group.edit.name': 'Nome do grupo',
  'bubble.deleteForMe': 'Apagar para mim',
  'bubble.save': 'Salvar imagem',
  'bubble.closeImage': 'Fechar imagem',
  'bubble.deleteForEveryone': 'Apagar para todos',
  'bubble.decryptFailed': 'Não foi possível descriptografar este anexo.',
  'bubble.decrypting': 'Descriptografando…',
  'bubble.state.pending': 'Pendente',
  'bubble.state.sent': 'Enviada',
  'bubble.state.delivered': 'Entregue',
  'bubble.state.read': 'Lida',
  'bubble.state.failed': 'Falhou',

  'attach.label': 'Anexar',
  'attach.camera': 'Câmera',
  'attach.image': 'Foto',
  'attach.video': 'Vídeo',
  'attach.audio': 'Áudio',
  'attach.document': 'Documento',
  'attach.error.title': 'Não foi possível anexar',

  'media.play': 'Reproduzir',
  'media.pause': 'Pausar',
  'media.seek': 'Posição na gravação',
  'media.decrypting': 'Descriptografando…',
  'media.permission.library': 'Sem permissão para acessar a galeria de fotos',
  'media.permission.camera': 'Sem permissão para acessar a câmera',
  'media.permission.microphone': 'Sem permissão para acessar o microfone',

  'invite.title': 'Convidar amigos',
  'invite.close': 'Fechar',
  'invite.body':
    'Envie o link. Quem abrir verá que você o convidou, e a conversa de vocês estará esperando assim que a pessoa se cadastrar.',
  'invite.preparing': 'Preparando um link…',
  'invite.share': 'Compartilhar',
  'invite.copy': 'Copiar link',
  'invite.copied': 'Copiado',
  'invite.copyFailed': 'Não foi possível copiar — selecione o link e copie manualmente',
  'invite.caution':
    'O link vale 30 dias e pode ser reutilizado. Qualquer pessoa que o tenha pode se cadastrar e ver que você a convidou — não publique em lugar público.',
  'invite.message': '{name} está convidando você para o VAV — mensagens criptografadas de ponta a ponta.',
  'invite.defaultName': 'Um amigo',

  'newChat.title': 'Nova conversa',
  'newChat.search': 'Nome de usuário, nome completo ou número de telefone',
  'newChat.searchByUsername': 'Buscar por nome de usuário',
  'newChat.direct': 'Conversa',
  'newChat.group': 'Grupo',
  'newChat.groupName': 'Nome do grupo',
  'newChat.pickHint': 'Escolha os participantes nos resultados — quantos quiser.',
  'newChat.searching': 'Buscando…',
  'newChat.noResults': 'Nenhum usuário encontrado com esse nome.',
  'newChat.createGroup': 'Criar grupo',
  'newChat.openChat': 'Abrir conversa',

  'phone.title': 'Número de telefone',
  'phone.privacy':
    'Seu número vira um hash no seu aparelho antes de ser enviado. O servidor guarda apenas a impressão digital, nunca o número.',
  'phone.mine': 'Meu número — para que possam me encontrar',
  'phone.save': 'Salvar',
  'phone.saved': 'Salvo',
  'phone.findFriends': 'Encontrar amigos',
  'phone.fromContacts': 'Escolher dos contatos',
  'phone.noContactPicker':
    'Este navegador não permite acesso aos contatos. Digite um número para verificar, ou use o Chrome no Android.',
  'phone.checkOne': 'Verificar um número',
  'phone.search': 'Buscar',
  'phone.noneRegistered': 'Nenhum desses números está cadastrado.',
  'phone.invalid': 'Isso não parece um número de telefone',

  'safety.title': 'Número de segurança',
  'safety.compare':
    'Compare este número com {name} por outro canal — pessoalmente ou por telefone. Se os números forem iguais, não há ninguém no meio.',
  'safety.confirm': 'Verifiquei este número',
  'safety.changeNote':
    'Se {name} reinstalar o aplicativo, o número vai mudar e você será avisado. Uma mudança inesperada vale uma conferida antes de continuar enviando.',
  'safety.noSession':
    'Ainda não foram trocadas chaves com {name}. Envie uma mensagem e volte aqui.',

  'common.cancel': 'Cancelar',
  'push.channel': 'Mensagens',
  'push.newMessage': 'Nova mensagem',
  'language.title': 'Idioma',
  'language.note': 'Detectado pelo seu navegador. Mude aqui se o palpite estiver errado.',
  'language.restart': 'Reinicie o aplicativo para concluir a mudança de direção do layout.',
  'app.failed.title': 'Não foi possível iniciar',
  'app.failed.retry': 'Tentar de novo',
  'phone.invalidHint': 'Tente o formato internacional completo, começando pelo código do seu país.',
  'phone.country': 'País',
  'phone.national': 'Seu número, como você discaria localmente',
  'phone.willSave': 'Será salvo como {number}',
  'phone.searchCountry': 'Buscar país',
  'phone.importContacts': 'Buscar nos meus contatos',
  'phone.importing': 'Verificando seus contatos…',
  'phone.importingProgress': 'Verificando seus contatos… {done} de {total}',
  'phone.importScanning': 'Lendo seus contatos…',
  'phone.importScanned': '{entries} entradas, {unique, plural, one {# número para verificar} other {# números para verificar}}',
  'phone.contactsDenied': 'A permissão para ler os contatos foi negada. Você ainda pode verificar um número manualmente.',
  'phone.matched': '{count, plural, =0 {Nenhum dos seus contatos está aqui ainda} one {# contato está aqui} other {# contatos estão aqui}}',
  'phone.contactsPrivacy': 'Seus contatos viram hash neste aparelho. A agenda em si nunca é enviada.',
  'invite.share.subject': 'Entre comigo no VAV',
};
